import type { CatalogPiece } from "./catalog-types";

/**
 * Three-level catalog taxonomy (handoff §7.2): macro category, canonical category, type.
 *
 * The table is ultimately server content, since the internal admin composes it. It lives here
 * only because the catalog contract does not return a macro category yet
 * (`docs/backend-requests.md`, item 12).
 *
 * **Why `MACROS` holds a single entry (`colecoes`) — read before "completing" it.**
 * The designer's table (handoff line 85) lists eight departments with their canonical categories,
 * and line 86 foresees an alias layer on top of it ("copo e taça" → Copo). That layer was never
 * delivered, so the table is half of what the designer meant. Matching is by **exact** name
 * (`canonicalCategory`), and the ERP does not speak the table's names: the table has "Xícara",
 * the ERP sends "XÍCARA DE CHÁ E PIRES" (27 products) and "XÍCARA DE CAFÉ E PIRES" (25), and
 * neither matches. Measured over the catalog: **16 of its 95 categories are recognised**. A
 * "Chá & Café" department would therefore gather AÇUCAREIRO and leave both cups out — a tea and
 * coffee department without a cup, which is worse than no department: a filter that lies. Choosing
 * any department would also hide 71% of the products.
 *
 * So the eight departments are not offered, the aliases are not indexed either (or the category
 * rail would keep mixing "Copo" with "MURANO"), and every category is shown under the ERP's own
 * name (`railCategory` falls back to it). What stays is "Todas as peças" and "Coleções", as agreed
 * with the designer for the MVP. The real table, **with** its aliases, is the products team's, after
 * the MVP. Do not reintroduce the eight names to fill a gap: the gap is the missing alias layer.
 */

/** Narrowed to the one department offered; see the note at the top of this file. */
export type MacroKey = "colecoes";

export type Macro = {
  key: MacroKey;
  label: string;
  /** Canonical categories the macro gathers. */
  categories: readonly string[];
};

/**
 * `colecoes` is the exception declared in the prototype: it groups no category, and its
 * second level becomes the piece's own collection.
 */
export const MACROS: readonly Macro[] = [{ key: "colecoes", label: "Coleções", categories: [] }];

/**
 * Data vocabularies that point at a canonical category — **kept exported, no longer indexed**.
 *
 * They would resolve "copo e taça", "acessório", "peça" and "castiçal" to categories of the
 * table the note at the top of this file explains is not offered. With the table gone from
 * `MACROS` they would still be canonised, and the category rail would go on mixing "Copo" with the
 * ERP's own "MURANO". They return to the index together with the real table (products team).
 */
export const CATEGORY_ALIASES: Readonly<Record<string, string>> = {
  "copo e taça": "Copo",
  acessório: "Peça de Servir",
  peça: "Peça de Servir",
  castiçal: "Vela e Castiçal",
};

function normalize(value: string): string {
  return value.trim().toLowerCase();
}

/** Canonical index, built once. */
const CATEGORY_INDEX: { canonical: Map<string, string>; macroOf: Map<string, MacroKey> } =
  (() => {
    const canonical = new Map<string, string>();
    const macroOf = new Map<string, MacroKey>();

    for (const macro of MACROS) {
      for (const category of macro.categories) {
        canonical.set(normalize(category), category);
        macroOf.set(category, macro.key);
      }
    }

    // `CATEGORY_ALIASES` is deliberately not indexed here (see its docblock).

    return { canonical, macroOf };
  })();

/** A piece's canonical category, or null when the vocabulary is unknown. */
export function canonicalCategory(piece: Pick<CatalogPiece, "category">): string | null {
  return CATEGORY_INDEX.canonical.get(normalize(piece.category)) ?? null;
}

/** A piece's macro category, derived from the canonical one. */
export function macroOf(piece: Pick<CatalogPiece, "category">): MacroKey | null {
  const category = canonicalCategory(piece);

  return category === null ? null : CATEGORY_INDEX.macroOf.get(category) ?? null;
}

/** A macro's categories; with no macro, every known category. */
export function categoriesFor(macro: MacroKey | null): readonly string[] {
  if (macro === null) return MACROS.flatMap((entry) => entry.categories);

  return MACROS.find((entry) => entry.key === macro)?.categories ?? [];
}
